import {isFeatureEnabled} from 'feature-flags';
import {Action, openDialog, TabProps, Tabs} from 'platform/components';
import {Box, Show} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useState} from 'react';

import {isNil, isNotEmpty, last} from 'ramda';

import {EntityResourceIds} from '@dms/api/shared';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';

import {buildArray, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {useIsCommunicationReadStateEnabled} from '../../hooks/useIsCommunicationReadStateEnabled';
import {CommunicationChannelChips} from '../CommunicationThread/components/CommunicationChannelChips';
import {CommunicationThreadFeed} from '../CommunicationThread/components/CommunicationThreadFeed';
import {CommunicationThreadScopeChips} from '../CommunicationThread/components/CommunicationThreadScopeChips';
import {RunOnMount} from '../CommunicationThread/components/RunOnMount';
import {useCommunicationThread} from '../CommunicationThread/hooks/useCommunicationThread';
import {useThreadCallActions} from '../CommunicationThread/hooks/useThreadCallActions';
import {useThreadReadState} from '../CommunicationThread/hooks/useThreadReadState';
import {CommunicationThreadScope} from '../CommunicationThread/types/CommunicationThreadScope';
import type {DmsDataGridProps} from '../DmsDataGrid/DmsDataGrid';
import {Main} from '../Main/Main';
import {SendCustomerEmailForm} from '../SendCustomerEmailForm/SendCustomerEmailForm';
import {SendCustomerSmsForm} from '../SendCustomerSmsForm/SendCustomerSmsForm';
import {CommunicationMessageHistory} from './CommunicationMessageHistory';

interface CommunicationSectionProps
  extends RequiredTestIdProps, Pick<DmsDataGridProps, 'maximization'> {
  customerId: string;
  recordId: string;
  resourceId: EntityResourceIds;
  /**
   * Shows a button on every thread message opening the record it is linked to. Enabled on the
   * customer detail, where the thread aggregates messages from records spread across the app.
   */
  isContextLinkVisible?: boolean;
  /** Scrolls the thread to the oldest message that is new for this visit, e.g. from the Kanban. */
  scrollToOldestUnread?: boolean;
  /**
   * Called once the scroll request is handled (the thread scrolled, or its request settled with
   * nothing new to scroll to), so the host can clear a one-time request. A thread that is not
   * requested yet (no customer) does not count as handled.
   */
  onScrollToOldestUnreadHandled?: () => void;
}

export function CommunicationSection(props: CommunicationSectionProps) {
  const isThreadEnabled = isFeatureEnabled(featureFlags.MESSAGE_SYNCHRONIZATION);
  const isReadStateEnabled = useIsCommunicationReadStateEnabled();

  // On the customer detail the customer itself is the host record, so there is no
  // narrower record scope to offer and the thread always shows everything.
  const isCustomerHostRecord = props.resourceId === EntityResourceIds.customer;

  const [threadScope, setThreadScope] = useState<CommunicationThreadScope>(
    isCustomerHostRecord ? 'customer' : 'record'
  );

  const threadCustomerId = isThreadEnabled ? props.customerId : '';

  const {callPairing, canEditCallNotes, canOpenCall, canListenRecording} = useThreadCallActions({
    resourceId: props.resourceId,
    recordId: props.recordId,
  });

  const threadArgs = match(threadScope)
    .with('customer', () => ({customerId: threadCustomerId}))
    .with('record', () => ({
      customerId: threadCustomerId,
      recordId: props.recordId,
      resourceId: props.resourceId,
    }))
    .exhaustive();

  const {activeFilter, setActiveFilter, filterCounts, groups, isLoading, isError, isSettled} =
    useCommunicationThread(threadArgs);

  // Only the messages the active channel filter shows count as seen.
  const shownMessages = groups.flatMap((group) => group.messages);
  const {readState, pendingOpenedIds, openPendingMessages} = useThreadReadState(shownMessages, {
    customerId: threadCustomerId,
    resourceId: props.resourceId,
    recordId: props.recordId,
  });

  // The feed is newest first, so the last message marked new is the oldest one.
  const oldestNewMessageId = last(
    shownMessages.filter((message) => readState.newMessageIds.includes(message.id))
  )?.id;
  const scrollToMessageId =
    isReadStateEnabled && props.scrollToOldestUnread ? oldestNewMessageId : undefined;

  const handleNewEmail = () =>
    openDialog(
      <SendCustomerEmailForm
        customerId={props.customerId}
        recordId={props.recordId}
        resourceId={props.resourceId}
      />,
      {withAdditionalFooter: true, size: 'large', title: i18n.t('general.actions.sendEmail')}
    );

  const handleNewSms = () =>
    openDialog(
      <SendCustomerSmsForm
        customerId={props.customerId}
        recordId={props.recordId}
        resourceId={props.resourceId}
      />,
      {scrollBehavior: 'outside', size: 'large', title: i18n.t('general.labels.smsMessage')}
    );

  const threadActions = buildArray<Action>()
    .whenNot(isCustomerHostRecord, {
      type: 'custom',
      component: (
        <CommunicationThreadScopeChips
          size="default"
          value={threadScope}
          onChange={setThreadScope}
          data-testid={suffixTestId('thread-scope', props)}
        />
      ),
    })
    .whenNot(isCustomerHostRecord, {
      type: 'custom',
      component: <Box height={8} borderLeft="1px solid" borderColor="general.separator" />,
    })
    .add({
      type: 'custom',
      component: (
        <CommunicationChannelChips
          size="default"
          filterCounts={filterCounts}
          activeFilter={activeFilter}
          onFilterChange={setActiveFilter}
        />
      ),
    })
    .add({
      type: 'custom',
      component: <Box height={8} borderLeft="1px solid" borderColor="general.separator" />,
    })
    .add({
      type: 'dropdown-button',
      variant: 'primary',
      size: 'default',
      leftIcon: 'content/add',
      title: i18n.t('general.labels.pureNewMessage'),
      'data-testid': suffixTestId('thread-newMessage', props),
      menuItems: [
        {
          label: i18n.t('general.actions.sendEmail'),
          leftIcon: 'communication/email',
          onClick: handleNewEmail,
        },
        {
          label: i18n.t('general.actions.sendSms'),
          leftIcon: 'communication/textsms',
          onClick: handleNewSms,
        },
      ],
    });

  const messageHistory = (
    <CommunicationMessageHistory
      maximization={props.maximization}
      customerId={props.customerId}
      presetInstanceId={suffixTestId('message-history', props)}
      data-testid={suffixTestId('message-history-datagrid', props)}
    />
  );

  const tabs: TabProps[] = [
    {
      id: 'thread',
      title: i18n.t('page.customer.labels.communicationThread'),
      actions: threadActions,
      content: (
        <Box height="100%" overflow="auto">
          <Box paddingBottom={4}>
            <Show when={isReadStateEnabled && isNotEmpty(pendingOpenedIds)}>
              {/* Remounts, and so sends again, whenever newly shown unread messages appear. */}
              <RunOnMount key={pendingOpenedIds.join()} onMount={openPendingMessages} />
            </Show>
            <Show when={props.scrollToOldestUnread && isSettled && isNil(scrollToMessageId)}>
              {/* Nothing new to scroll to: the one-time request is handled all the same. */}
              <RunOnMount onMount={() => props.onScrollToOldestUnreadHandled?.()} />
            </Show>
            <CommunicationThreadFeed
              readState={isReadStateEnabled ? readState : undefined}
              scrollToMessageId={scrollToMessageId}
              onScrolledToMessage={props.onScrollToOldestUnreadHandled}
              groups={groups}
              isLoading={isLoading}
              isError={isError}
              callPairing={callPairing}
              canEditCallNotes={canEditCallNotes}
              canOpenCall={canOpenCall}
              canListenRecording={canListenRecording}
              contextLinkHostRecord={
                props.isContextLinkVisible
                  ? {recordId: props.recordId, resourceId: props.resourceId}
                  : undefined
              }
            />
          </Box>
        </Box>
      ),
      'data-testid': suffixTestId('thread', props),
    },
    {
      id: 'messageHistory',
      title: i18n.t('general.labels.emailActivityLogs'),
      content: messageHistory,
      'data-testid': suffixTestId('message-history', props),
    },
  ];

  return (
    <Main isFullHeight>
      {isThreadEnabled ? (
        <Tabs
          variant="fullHeight"
          isFullHeight
          isLazy
          tabs={tabs}
          data-testid={suffixTestId('tabs', props)}
        />
      ) : (
        messageHistory
      )}
    </Main>
  );
}
